import type { CustomersContextProps } from '@src/providers/CustomersContext.types';
import type {
  AllInTier,
  ColumnDef,
  Customer,
  CustomerConfiguration,
  FeeTier,
  InputsAndDropdownsDrawerOption,
  SymbolGroup,
} from '@talos/kyoko';
import { Alert, AlertVariants, getTierDrawerOptions } from '@talos/kyoko';
import { set } from 'lodash-es';
import { createElement } from 'react';
import {
  CREATE_CUSTOMER_DISPLAY_NAME_MAX_LENGTH,
  CREATE_CUSTOMER_NAME_MAX_LENGTH,
  type CreateCustomerValidationIndex,
  validateCreateCustomerDisplayName,
  validateCreateCustomerName,
} from '../Dialogs/CreateCustomerDrawer.utils';

/** Extended record that includes DisplayName for display and editing */
export interface CustomerConfigurationWithDisplayName extends CustomerConfiguration {
  DisplayName?: string;
}

export interface CustomerConfigurationDrawerRecord extends CustomerConfigurationWithDisplayName {
  Name?: string;
  // Synthetic field for the create drawer's info custom component; it has no form value.
  _creationInfo?: never;
}

type CustomerConfigurationDrawerOption = InputsAndDropdownsDrawerOption<CustomerConfigurationDrawerRecord>;
const CUSTOMER_CONFIGURATION_DRAWER_PATCH_FIELDS = [
  'DisplayName',
  'Mode',
  'AllInTier',
  'FeeTier',
  'SymbolGroup',
] as const satisfies readonly (keyof CustomerConfigurationDrawerRecord)[];

export function getCustomerConfigurationDrawerRecord(
  customerConfiguration: CustomerConfiguration,
  getDisplayName: (customer: { Counterparty: string }) => string
): CustomerConfigurationDrawerRecord {
  return {
    ...customerConfiguration,
    DisplayName: getDisplayName(customerConfiguration),
  };
}

export function getCustomerConfigurationPatchPayload(
  entity: CustomerConfigurationWithDisplayName
): CustomerConfiguration {
  const { DisplayName, ...customerConfiguration } = entity;
  return customerConfiguration;
}

export function mergeCustomerConfigurationDrawerFormIntoRecord(
  customerConfiguration: CustomerConfigurationDrawerRecord,
  drawerForm: Partial<CustomerConfigurationDrawerRecord>
): CustomerConfigurationDrawerRecord {
  const mergedConfiguration = { ...customerConfiguration };

  CUSTOMER_CONFIGURATION_DRAWER_PATCH_FIELDS.forEach(field => {
    const value = drawerForm[field];
    if (value != null) {
      set(mergedConfiguration, field, value === '' ? null : value);
    }
  });

  return mergedConfiguration;
}

/**
 * Strip DisplayName from the CustomerConfiguration PATCH request,
 * and update it separately via the Customer API if it changed.
 * Returns an object with the stripped config and a function to inject DisplayName into the response.
 */
export function getEntityForPatchPost(
  entity: CustomerConfigurationWithDisplayName,
  customersByName: Map<string, Customer> | undefined,
  updateCustomer: CustomersContextProps['updateCustomer']
): CustomerConfiguration {
  const { DisplayName } = entity;

  // Update DisplayName separately via Customer API if it changed
  const originalDisplayName = customersByName?.get(entity.Counterparty)?.DisplayName ?? '';
  if (DisplayName !== originalDisplayName && DisplayName !== undefined) {
    updateCustomer({ Name: entity.Counterparty, DisplayName });
  }

  return getCustomerConfigurationPatchPayload(entity);
}

export const CUSTOMER_CONFIGURATIONS_COLUMNS: ColumnDef<CustomerConfigurationWithDisplayName>[] = [
  { field: 'DisplayName', title: 'Customer', type: 'text', sortable: true, initialSort: '+' },
  { field: 'Counterparty', title: 'Counterparty', type: 'text', initialHide: true },
  { field: 'AllInTier', type: 'text', title: 'Base Tier' },
  { field: 'FeeTier', type: 'text', title: 'Fee Tier' },
  { field: 'SymbolGroup', type: 'text', title: 'Symbol Group' },
  { field: 'UpdateUser', type: 'text', initialHide: true },
  { field: 'Revision', type: 'text', initialHide: true },
];

export const CUSTOMER_CONFIGURATIONS_FILTERABLE_SERVER_FIELDS: (keyof CustomerConfiguration)[] = ['Counterparty'];

export const CUSTOMER_CONFIGURATIONS_BULK_EDIT_FIELDS: (keyof CustomerConfiguration)[] = [
  'Mode',
  'AllInTier',
  'FeeTier',
  'SymbolGroup',
];

type CustomerTieringDrawerOptions = {
  allInTierOptions: AllInTier[];
  feeTierOptions: FeeTier[];
  symbolGroupOptions: SymbolGroup[];
};

type CreateCustomerDrawerOptionsProps = {
  customerValidationIndex: CreateCustomerValidationIndex;
  showCustomerTiering: boolean;
} & Partial<CustomerTieringDrawerOptions>;

const CUSTOMER_TIERING_FIELD_METADATA = {
  AllInTier: {
    title: 'Base Tier',
    guideText: 'All-in pricing tier defining spread-based pricing and markup applied to this customer.',
  },
  FeeTier: {
    title: 'Fee Tier',
    guideText: "Fee tier defining transaction fee rates applied to this customer's trades.",
  },
  SymbolGroup: {
    title: 'Symbol Group',
    guideText: 'Symbol group that restricts which instruments this customer is permitted to trade.',
  },
} as const;

export function getEditCustomerDrawerOptions({
  showCustomerTiering,
  allInTierOptions,
  feeTierOptions,
  symbolGroupOptions,
}: {
  showCustomerTiering: boolean;
  allInTierOptions: AllInTier[];
  feeTierOptions: FeeTier[];
  symbolGroupOptions: SymbolGroup[];
}): CustomerConfigurationDrawerOption[] {
  return [
    {
      field: 'Counterparty',
      title: 'Customer',
      type: 'input',
      guideText: 'The customer (counterparty) this configuration belongs to. Cannot be changed after creation.',
      disabledWhenEditing: true,
    },
    {
      field: 'DisplayName',
      title: 'Display Name',
      type: 'input',
    },
    {
      field: 'Mode',
      title: 'Enabled',
      type: 'inlineToggle',
      guideText: 'Controls whether this customer is enabled.',
    },
    ...(showCustomerTiering
      ? ([
          {
            field: 'AllInTier',
            type: 'dropdown',
            ...CUSTOMER_TIERING_FIELD_METADATA.AllInTier,
            getDropdownOptions: () => getTierDrawerOptions(allInTierOptions),
            showClear: true,
          },
          {
            field: 'FeeTier',
            type: 'dropdown',
            ...CUSTOMER_TIERING_FIELD_METADATA.FeeTier,
            getDropdownOptions: () => getTierDrawerOptions(feeTierOptions),
            showClear: true,
          },
          {
            field: 'SymbolGroup',
            type: 'dropdown',
            ...CUSTOMER_TIERING_FIELD_METADATA.SymbolGroup,
            getDropdownOptions: () => getTierDrawerOptions(symbolGroupOptions),
            showClear: true,
          },
        ] satisfies CustomerConfigurationDrawerOption[])
      : []),
  ] satisfies CustomerConfigurationDrawerOption[];
}

export function getCreateCustomerDrawerOptions({
  customerValidationIndex,
  showCustomerTiering,
  allInTierOptions,
  feeTierOptions,
  symbolGroupOptions,
}: CreateCustomerDrawerOptionsProps): CustomerConfigurationDrawerOption[] {
  return [
    {
      field: 'Name',
      title: 'Name',
      type: 'input',
      placeholder: `Up to ${CREATE_CUSTOMER_NAME_MAX_LENGTH} characters`,
      getIsRequired: () => true,
      validate: form => validateCreateCustomerName(form.Name ?? '', customerValidationIndex),
    },
    {
      field: 'DisplayName',
      title: 'Display Name',
      type: 'input',
      placeholder: `Up to ${CREATE_CUSTOMER_DISPLAY_NAME_MAX_LENGTH} characters`,
      getIsRequired: () => true,
      validate: form => validateCreateCustomerDisplayName(form.DisplayName ?? ''),
    },
    ...(showCustomerTiering
      ? ([
          {
            field: 'AllInTier',
            type: 'dropdown',
            ...CUSTOMER_TIERING_FIELD_METADATA.AllInTier,
            getDropdownOptions: () => getTierDrawerOptions(allInTierOptions ?? []),
            showClear: true,
          },
          {
            field: 'FeeTier',
            type: 'dropdown',
            ...CUSTOMER_TIERING_FIELD_METADATA.FeeTier,
            getDropdownOptions: () => getTierDrawerOptions(feeTierOptions ?? []),
            showClear: true,
          },
          {
            field: 'SymbolGroup',
            type: 'dropdown',
            ...CUSTOMER_TIERING_FIELD_METADATA.SymbolGroup,
            getDropdownOptions: () => getTierDrawerOptions(symbolGroupOptions ?? []),
            showClear: true,
          },
        ] satisfies CustomerConfigurationDrawerOption[])
      : []),
    {
      field: '_creationInfo',
      type: 'custom',
      useCustomFormGroup: true,
      renderer: () =>
        createElement(
          Alert,
          { variant: AlertVariants.Info, dismissable: false },
          'Customer Account will automatically be enabled when the customer is created.'
        ),
    } satisfies CustomerConfigurationDrawerOption,
  ] satisfies CustomerConfigurationDrawerOption[];
}

export function getCustomerConfigurationsPatchDeletePath(entity: CustomerConfiguration) {
  return `/customers/${encodeURIComponent(entity.Counterparty)}/configuration`;
}
